'use client';

import { useState, useEffect } from 'react';
import { useI18n } from '@/app/components/LanguageProvider';
import { submitForm, resetForm } from '@/lib/form-utils';

export default function PartnerForm() {
    const { t, loadComponentTranslations } = useI18n();
    const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

    useEffect(() => {
        void loadComponentTranslations('PartnerForm');
    }, [loadComponentTranslations]);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setStatus('submitting');

        const formData = new FormData(event.currentTarget);
        const result = await submitForm('/api/partners/apply', formData);
        
        if (result.success) {
            setStatus('success');
            resetForm(event.currentTarget);
            
            setTimeout(() => {
                setStatus('idle');
            }, 5000);
        } else {
            setStatus('error');
        }
    };

    return (
        <div className="relative max-w-3xl mx-auto p-px rounded-3xl bg-linear-to-b from-purple-500/30 to-transparent">
            <div className="relative p-8 md:p-12 glass-rounded-3xl glass-form">
                {status === 'success' ? (
                    <div className="text-center py-12">
                        <h3 className="text-2xl font-bold text-green-400 mb-2">{t('formSuccessTitle', 'PartnerForm')}</h3>
                        <p className="text-neutral-300">{t('formSuccessMessage', 'PartnerForm')}</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="fullName" className="block text-sm font-medium text-neutral-300 mb-2">{t('formFullName', 'PartnerForm')}</label>
                                <input
                                    type="text"
                                    name="fullName"
                                    id="fullName"
                                    required
                                    className="w-full glass-input glass-rounded px-4 py-2 text-white focus:ring-2 focus:ring-purple-500 focus:outline-none glass-transition"
                                />
                            </div>
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-neutral-300 mb-2">{t('formEmail', 'PartnerForm')}</label>
                                <input
                                    type="email"
                                    name="email"
                                    id="email"
                                    required
                                    className="w-full glass-input glass-rounded px-4 py-2 text-white focus:ring-2 focus:ring-purple-500 focus:outline-none glass-transition"
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="projectLink" className="block text-sm font-medium text-neutral-300 mb-2">{t('partnerFormProjectLink', 'PartnerForm')}</label>
                            <input
                                type="url"
                                name="projectLink"
                                id="projectLink"
                                placeholder="https://your-project.com"
                                required
                                className="w-full glass-input glass-rounded px-4 py-2 text-white focus:ring-2 focus:ring-purple-500 focus:outline-none glass-transition"
                            />
                        </div>

                        <div>
                            <label htmlFor="coverLetter" className="block text-sm font-medium text-neutral-300 mb-2">{t('formCoverLetter', 'PartnerForm')}</label>
                            <textarea
                                name="coverLetter"
                                id="coverLetter"
                                rows={5}
                                required
                                placeholder={t('partnerFormCoverLetterPlaceholder', 'PartnerForm')}
                                className="w-full glass-input glass-rounded px-4 py-2 text-white focus:ring-2 focus:ring-purple-500 focus:outline-none glass-transition"
                            ></textarea>
                        </div>

                        <div className="text-center pt-4">
                            <button
                                type="submit"
                                disabled={status === 'submitting'}
                                className="inline-block bg-purple-600 text-white font-semibold px-10 py-3 rounded-lg hover:bg-purple-700 transition-colors duration-300 transform hover:scale-105 disabled:bg-gray-500 disabled:scale-100 disabled:cursor-not-allowed"
                            >
                                {status === 'submitting' ? t('formSubmitting', 'PartnerForm') : t('partnerFormSubmitButton', 'PartnerForm')}
                            </button>
                            {status === 'error' && <p className="text-red-400 mt-4">{t('formErrorMessage', 'PartnerForm')}</p>}
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
